// Logica pura de lo que la cabecera y la barra lateral del portal muestran
// sobre la encuesta de ofertas (rediseño, docs/PLAN-REDISENO-PORTAL.md §6.2 y
// §13): la hora de la planta, el indicador "Ofertas al día" y el contador de
// Viajes. Sin React ni reloj ambiente: `nowMs` llega por parametro.
//
// Todo sale de la encuesta unica de ofertas (PP-71): sus `sites` traen la
// zona IANA de cada planta habilitada, aunque no tenga ofertas, y sus `items`
// lo que espera una accion del proveedor. Ninguna lectura nueva.
import type { ProviderMultiSiteSite, ProviderMultiSiteTrip } from "@ucorp/contracts";
import { asProviderTrip } from "./home.js";
import { formatClock, tripActions } from "./requests.js";

/** Un reloj de la cabecera: una zona IANA y las plantas que la usan. */
export interface PlantClock {
  readonly timezone: string;
  /** HH:MM en la zona de la planta (regla 9), nunca la del navegador. */
  readonly time: string;
  /** Nombres de las plantas de esa zona, en el orden de la encuesta. */
  readonly siteNames: readonly string[];
}

/**
 * Relojes de la cabecera para el filtro de planta (`null` es "Todas").
 *
 * Con una planta filtrada, un solo reloj: el de su zona. Con "Todas", uno por
 * zona distinta: si todas las plantas comparten zona (el caso comun) es un
 * solo reloj; si no, cada hora va con los nombres de sus plantas al lado,
 * para que nadie lea la hora de una planta como la de otra. Una planta que
 * la cuenta no tiene (`allowedSiteIds`) no cuenta, como en el resto del portal.
 * Una zona que el navegador no conoce no se pinta: mejor sin hora que con la
 * del navegador.
 */
export function plantClocks(
  sites: readonly ProviderMultiSiteSite[],
  allowedSiteIds: ReadonlySet<string>,
  siteId: string | null,
  nowMs: number,
): PlantClock[] {
  const instant = new Date(nowMs).toISOString();
  const byZone = new Map<string, string[]>();
  for (const site of sites) {
    if (!allowedSiteIds.has(site.siteId)) continue;
    if (siteId !== null && site.siteId !== siteId) continue;
    const names = byZone.get(site.timezone) ?? [];
    if (!names.includes(site.siteName)) names.push(site.siteName);
    byZone.set(site.timezone, names);
  }
  const clocks: PlantClock[] = [];
  for (const [timezone, siteNames] of byZone) {
    const time = formatClock(instant, timezone);
    if (time !== "") clocks.push({ timezone, time, siteNames });
  }
  return clocks;
}

/** Lo que espera al proveedor con el reloj todavia corriendo: ofertas por responder y aceptadas por asignar. */
export interface PendingCounts {
  readonly respond: number;
  readonly assign: number;
}

export function pendingCounts(rows: readonly ProviderMultiSiteTrip[], nowMs: number): PendingCounts {
  let respond = 0;
  let assign = 0;
  for (const row of rows) {
    const actions = tripActions(asProviderTrip(row), nowMs);
    if (actions.respond) respond += 1;
    if (actions.assign) assign += 1;
  }
  return { respond, assign };
}

/** Lo que dice el indicador de ofertas de la cabecera. */
export type OffersStatus =
  | { readonly kind: "cargando" }
  /** La encuesta esta sana y no hay ofertas sin responder. */
  | { readonly kind: "al-dia" }
  | { readonly kind: "por-responder"; readonly count: number }
  /** Sin conexion, o la ultima vuelta fallo: lo que se ve puede no ser lo de ahora. */
  | { readonly kind: "sin-actualizar" };

export interface OffersFeedSnapshot {
  readonly estado: "cargando" | "denegado" | "error" | "listo";
  /** La ultima vuelta fallo y se muestra la anterior. */
  readonly stale: boolean;
}

/**
 * Estado del indicador. "Al día" solo se dice con la encuesta sana: sin
 * conexion o con la ultima vuelta fallida no se puede afirmar que no haya
 * ofertas nuevas, aunque las ultimas recibidas digan cero. Sin acceso no hay
 * indicador (`null`): el portal ya dice que la sesion no sirve.
 */
export function offersStatus(feed: OffersFeedSnapshot, toRespond: number, online: boolean): OffersStatus | null {
  if (feed.estado === "denegado") return null;
  if (feed.estado === "cargando") return online ? { kind: "cargando" } : { kind: "sin-actualizar" };
  if (feed.estado === "error" || feed.stale || !online) return { kind: "sin-actualizar" };
  return toRespond > 0 ? { kind: "por-responder", count: toRespond } : { kind: "al-dia" };
}

export function offersStatusText(status: OffersStatus): string {
  switch (status.kind) {
    case "cargando":
      return "Consultando ofertas...";
    case "al-dia":
      return "Ofertas al día";
    case "por-responder":
      return status.count === 1 ? "1 oferta por responder" : `${String(status.count)} ofertas por responder`;
    case "sin-actualizar":
      return "Ofertas sin actualizar";
  }
}

/** Texto accesible del contador de Viajes. */
export function pendingTripsLabel(count: number): string {
  return count === 1 ? "1 viaje pendiente" : `${String(count)} viajes pendientes`;
}

/** Lo que entra en el contador: hasta 99, despues "99+". */
export const BADGE_MAX = 99;

export function badgeText(count: number): string {
  return count > BADGE_MAX ? `${String(BADGE_MAX)}+` : String(count);
}
